import { useState, useEffect } from "react";
import { Button } from "./ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "./ui/tooltip";
import { Info } from "lucide-react";

const ROICalculator = () => {
  const [wage, setWage] = useState("18");
  const [hours, setHours] = useState("4");
  const [days, setDays] = useState("6");
  
  const [weekly, setWeekly] = useState(432);
  const [monthly, setMonthly] = useState(1871);
  const [annual, setAnnual] = useState(22464);

  const formatMoney = (n: number) => {
    return isFinite(n) ? `$${n.toLocaleString(undefined, { maximumFractionDigits: 0 })}` : "$0";
  };

  const calculate = () => {
    const w = parseFloat(wage) || 0;
    const h = parseFloat(hours) || 0;
    const d = parseFloat(days) || 0;

    const weeklyCalc = w * h * d;
    const monthlyCalc = weeklyCalc * 4.33;
    const annualCalc = monthlyCalc * 12;

    setWeekly(weeklyCalc);
    setMonthly(monthlyCalc);
    setAnnual(annualCalc);
  };

  // Real-time calculation on input change
  useEffect(() => {
    calculate();
  }, [wage, hours, days]);

  return (
    <div 
      className="rounded-xl p-4 md:p-5 border border-[hsl(var(--line))] hover:border-primary/30 transition-all duration-500 animate-fade-in"
      style={{
        background: "linear-gradient(180deg, hsl(var(--glass)), hsl(0 0% 100% / 0.03))",
        boxShadow: "0 4px 20px rgba(0,0,0,0.2), inset 0 0 40px hsl(var(--primary) / 0.05)"
      }}
    >
      <h3 className="text-lg md:text-xl font-bold mb-2 font-orbitron">ROI in 30 Seconds</h3>
      <p className="text-sm mb-3.5" style={{ color: "#dce7ff" }}>
        Estimate labor savings vs. robot cost. Instant, no email required.
      </p>

      <TooltipProvider>
        <div className="space-y-2.5">
          <div>
            <label className="flex items-center gap-2 mb-1.5 font-semibold text-sm">
              Hourly wage ($)
              <Tooltip>
                <TooltipTrigger asChild>
                  <Info className="w-4 h-4 text-muted-foreground cursor-help" />
                </TooltipTrigger>
                <TooltipContent>
                  <p className="max-w-xs">Average hourly wage of staff that will be replaced or assisted by automation</p>
                </TooltipContent>
              </Tooltip>
            </label>
            <input
              type="number"
              inputMode="decimal"
              placeholder="18"
              value={wage}
              onChange={(e) => setWage(e.target.value)}
              className="w-full px-3 py-2.5 rounded-lg border border-[hsl(var(--line))] bg-[hsl(var(--input))] text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary transition-all"
              aria-label="Hourly wage in dollars"
            />
          </div>

          <div>
            <label className="flex items-center gap-2 mb-1.5 font-semibold text-sm">
              Hours per day saved
              <Tooltip>
                <TooltipTrigger asChild>
                  <Info className="w-4 h-4 text-muted-foreground cursor-help" />
                </TooltipTrigger>
                <TooltipContent>
                  <p className="max-w-xs">Estimated labor hours saved per day through automation (delivery, cleaning, etc.)</p>
                </TooltipContent>
              </Tooltip>
            </label>
            <input
              type="number"
              inputMode="decimal"
              placeholder="4"
              value={hours}
              onChange={(e) => setHours(e.target.value)}
              className="w-full px-3 py-2.5 rounded-lg border border-[hsl(var(--line))] bg-[hsl(var(--input))] text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary transition-all"
              aria-label="Hours saved per day"
            />
          </div>

          <div>
            <label className="flex items-center gap-2 mb-1.5 font-semibold text-sm">
              Days per week
              <Tooltip>
                <TooltipTrigger asChild>
                  <Info className="w-4 h-4 text-muted-foreground cursor-help" />
                </TooltipTrigger>
                <TooltipContent>
                  <p className="max-w-xs">Number of days per week the automation solution will operate</p>
                </TooltipContent>
              </Tooltip>
            </label>
            <input
              type="number"
              inputMode="numeric"
              placeholder="6"
              value={days}
              onChange={(e) => setDays(e.target.value)}
              className="w-full px-3 py-2.5 rounded-lg border border-[hsl(var(--line))] bg-[hsl(var(--input))] text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary transition-all"
              aria-label="Days per week"
            />
          </div>
        </div>
      </TooltipProvider>

      <div className="flex gap-2.5 flex-wrap pt-1.5">
        <Button 
          asChild
          variant="outline" 
          className="glass-effect border-[hsl(var(--line))] hover:bg-white/10 hover:border-primary/50 transition-all duration-300"
        >
          <a href="https://api.leadconnectorhq.com/widget/bookings/neilaipudurobotics" target="_blank" rel="noopener noreferrer">
            Book Consultation
          </a>
        </Button>
      </div>

      <div className="grid grid-cols-3 gap-2 mt-3.5">
        <div className="border border-[hsl(var(--line))] rounded-xl bg-white/5 hover:bg-white/8 p-2.5 text-center transition-all duration-300 hover:border-primary/40">
          <div className="text-[0.65rem] opacity-85 mb-1">Weekly Savings</div>
          <div className="text-lg md:text-xl font-black">{formatMoney(weekly)}</div>
        </div>
        <div className="border border-[hsl(var(--line))] rounded-xl bg-white/5 hover:bg-white/8 p-2.5 text-center transition-all duration-300 hover:border-primary/40">
          <div className="text-[0.65rem] opacity-85 mb-1">Monthly Savings</div>
          <div className="text-lg md:text-xl font-black">{formatMoney(monthly)}</div>
        </div>
        <div className="border border-[hsl(var(--line))] rounded-xl bg-white/5 hover:bg-white/8 p-2.5 text-center transition-all duration-300 hover:border-primary/40">
          <div className="text-[0.65rem] opacity-85 mb-1">Annual Savings</div>
          <div className="text-lg md:text-xl font-black">{formatMoney(annual)}</div>
        </div>
      </div>

      <p className="text-xs mt-2 opacity-90" style={{ color: "#cfe1ff" }}>
        Estimates only. Book a demo for a site-specific quote.
      </p>
    </div>
  );
};

export default ROICalculator;
